import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Payment successful!');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      setStatus('error');
      toast.error('Failed to verify payment');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full glass-panel rounded-2xl p-8 text-center border">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#D4AF37' }} />
            <h1 className="text-2xl font-bold mb-2" data-testid="payment-checking">Verifying Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h1 className="text-2xl font-bold mb-2" data-testid="payment-success">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Your membership is now active. Welcome to Blendonics!
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full rounded-full"
              data-testid="go-to-dashboard-button"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-2xl font-bold mb-2 text-red-500" data-testid="payment-error">Payment Error</h1>
            <p className="text-muted-foreground mb-6">
              There was an issue verifying your payment. Please contact support.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="w-full rounded-full"
            >
              Back to Pricing
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h1 className="text-2xl font-bold mb-2" data-testid="payment-timeout">Verification Timeout</h1>
            <p className="text-muted-foreground mb-6">
              Payment verification is taking longer than expected. Please check your email for confirmation.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full rounded-full"
            >
              Continue to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
